import { Download, BookOpen, Presentation, FileLock2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL;
const SECTIONS = [
  ["The business model", "EON is request-first: clients describe the event (lane, date, guests, budget) and you answer with a scoped proposal. Six lanes — Trail Rides, Concerts, Corporate, Sponsorships, Vendor Booths, Artist Booking — each with a starting price so nobody wastes time."],
  ["Daily routine: Admin › Requests", "New briefs arrive with a reference number and the client already got a confirmation email. Reply within one business day, set status to quoted and enter the amount, then won or lost. Pipeline value shows at the top."],
  ["Create an event", "Admin › Events › New Event. Add ticket tiers (GA / Premium / VIP / Owner's Box), vendor booth types, hero image from Media, then Publish. Featured events get the homepage countdown."],
  ["Sponsorships", "/sponsorships shows the $5K–$50K tiers with projected ROI. Brands pay a 25% deposit by card and instantly receive a branded deck PDF; invoice the balance 30 days before the event."],
  ["Vendors", "Vendors reserve and pay at /vendors. Inventory is live and sells out automatically. Admin › Vendors is your load-in list."],
  ["Media, hero video, music", "Admin › Media: upload real photos tagged by lane (they drive the homepage tiles), generate the AI hero video from 3 photos or upload footage. Music streams through official Spotify players — legal and free."],
  ["Money", "Stripe collects every payment (≈2.9% + 30¢). Claim your Stripe account for bank payouts. Refunds happen in the Stripe dashboard."],
  ["First 30 days", "Week 1 photos + first event · Week 2 pitch 20 brands on sponsorship · Week 3 open vendor booths · Week 4 book the headliner and launch early-bird to the email list."],
];

export default function OwnerManualPage() {
  const navigate = useNavigate();
  const download = async (path, name) => {
    try {
      const r = await api.get(path, { responseType: "blob", params: { site: window.location.origin } });
      const url = URL.createObjectURL(r.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: name });
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast("Log in as the owner to download the confidential handoff.");
      navigate("/admin/login");
    }
  };
  const loggedIn = !!localStorage.getItem("eon_token");
  const downloadHandoff = () => download("/admin/owner-handoff.pdf", "EON-Owner-Handoff.pdf");
  const downloadManual = () => download("/owner-manual.pdf", "EON-Owner-Manual.pdf");
  return (
    <div className="mx-auto max-w-5xl px-4 py-14 sm:px-6 md:py-20" data-testid="owner-manual-page">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <span className="eyebrow">Owner's Manual</span>
          <h1 className="mt-2 font-display text-4xl uppercase text-chrome sm:text-5xl">How to run <span className="text-gold">EON Infinity.</span></h1>
          <p className="mt-3 max-w-xl text-neutral-400">Everything the owner needs to operate the platform — from first login to first sponsor cheque.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <a href={`${API}/api/client-deck.pdf`} target="_blank" rel="noreferrer" data-testid="deck-download-btn" className="btn-chrome flex items-center gap-2 rounded-full px-6 py-3.5 font-cond font-bold uppercase">
            <Presentation className="h-4 w-4" /> Client Walkthrough Deck
          </a>
          <button onClick={downloadHandoff} data-testid="handoff-download-btn" className="btn-chrome flex items-center gap-2 rounded-full px-6 py-3.5 font-cond font-bold uppercase">
            <FileLock2 className="h-4 w-4" /> Owner's Handoff (login required)
          </button>
          <button onClick={downloadManual} data-testid="manual-download-btn" className="btn-gold flex items-center gap-2 rounded-full px-6 py-3.5 font-cond font-bold uppercase">
            <Download className="h-4 w-4" /> {loggedIn ? "Download Manual PDF (with logins)" : "Download Manual PDF (login required)"}
          </button>
        </div>
      </div>
      <div className="mt-12 grid gap-4 md:grid-cols-2">
        {SECTIONS.map(([t, d], i) => (
          <div key={t} className="glass rounded-2xl p-6" data-testid={`manual-section-${i}`}>
            <div className="flex items-center gap-3"><span className="font-display text-2xl text-gold">{String(i + 1).padStart(2, "0")}</span><BookOpen className="h-4 w-4 text-neutral-500" /></div>
            <h2 className="mt-3 font-cond text-xl font-extrabold uppercase text-white">{t}</h2>
            <p className="mt-2 text-sm text-neutral-400">{d}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
